import { useQuery } from '@tanstack/react-query';
import { FolderKanban } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { useIsAdmin } from '../../api/session';
import type { ProjectSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { LinkButton } from '../../ui/Button';
import { EmptyState, QueryView } from '../../ui/feedback';
import { PageHeader, Tabs, usePageTitle } from '../../ui/misc';

/** PRJ-01/04: accessible active and archived projects. */
export function ProjectsPage() {
  usePageTitle('Projects');
  useCrumbs([{ label: 'Workspace' }, { label: 'Projects' }]);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'active' | 'archived' | 'all'>('active');
  const q = useQuery({ queryKey: ['projects'], queryFn: () => api.get<ProjectSummary[]>('/projects') });
  return (
    <div className="page">
      <PageHeader
        title="Projects"
        subtitle="Projects you can access. Archived projects stay readable."
        actions={
          admin && (
            <LinkButton to="/projects/new" variant="primary">
              Create project
            </LinkButton>
          )
        }
      />
      <QueryView query={q}>
        {(projects) => {
          const active = projects.filter((p) => !p.archived);
          const archived = projects.filter((p) => p.archived);
          const shown = tab === 'active' ? active : tab === 'archived' ? archived : projects;
          return (
            <>
              <Tabs
                label="Project state"
                value={tab}
                onChange={setTab}
                tabs={[
                  { value: 'active', label: 'Active', count: active.length },
                  { value: 'archived', label: 'Archived', count: archived.length },
                  { value: 'all', label: 'All', count: projects.length },
                ]}
              />
              {shown.length === 0 ? (
                <EmptyState
                  icon={FolderKanban}
                  title={projects.length ? 'No projects in this view' : 'No projects yet'}
                >
                  {admin
                    ? 'Create a project to start collecting requests.'
                    : 'You are not a member of any project yet. Ask an admin to add you.'}
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Key</th>
                        <th>Project</th>
                        <th>Your access</th>
                        <th className="num">Open</th>
                        <th className="num">Members</th>
                        <th>State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((p) => (
                        <tr key={p.id} className="clickable" onClick={() => navigate(`/projects/${p.key}`)}>
                          <td className="mono ink">
                            <a
                              href={`/projects/${p.key}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/projects/${p.key}`);
                              }}
                            >
                              {p.key}
                            </a>
                          </td>
                          <td>
                            <div className="primary-cell">{p.name}</div>
                            {p.description && (
                              <div
                                className="small muted"
                                style={{
                                  maxWidth: 520,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {p.description}
                              </div>
                            )}
                          </td>
                          <td>{admin ? (p.isMember ? 'Admin · member' : 'Admin') : 'Member'}</td>
                          <td className="num">{p.openTickets}</td>
                          <td className="num">{p.members}</td>
                          <td>
                            {p.archived ? <Badge>Archived</Badge> : <Badge tone="success">Active</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
